import { Inject, Injectable, Optional } from '@angular/core';
import {
  InterpolateFunction,
  InterpolationParameters,
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateDefaultParser,
} from '@ngx-translate/core';

@Injectable()
export class TranslateParserService extends TranslateDefaultParser {
  overwrites: { [key: string]: string } = {};

  constructor(
    @Inject('TRANSLATION_KEY') @Optional() public prefix = 'resident_shared',
    @Inject('CUSTOM_TRANSLATIONS') @Optional() overwrites
  ) {
    super();
    if (overwrites && Array.isArray(overwrites)) {
      this.overwrites = {};
      for (let i = 0; i < overwrites.length; i++) {
        const overwrite = overwrites[i];
        this.overwrites[overwrite.key] = overwrite.value;
      }
    }
  }

  /**
   * Override interpolate to handle key transformation for interpolation parameters.
   */
  override interpolate(
    expr: InterpolateFunction | string,
    params?: InterpolationParameters
  ): string | undefined {
    return super.interpolate(expr, params);
  }

  parseKey(key: string) {
    const overwrite = this.overwrites[key];
    let query;
    if (overwrite) {
      query = overwrite;
    } else {
      query = `${this.prefix}.${key}`;
    }
    return query;
  }
}

/**
 * In v17, getValue was moved from TranslateDefaultParser to a standalone function.
 * This MissingTranslationHandler provides the key-prefixing behavior that was previously
 * achieved by overriding getValue in the parser.
 *
 * When a key is not found, this handler tries looking it up with the configured prefix.
 */
@Injectable()
export class PrefixedMissingTranslationHandler implements MissingTranslationHandler {
  constructor(
    @Inject('TRANSLATION_KEY') @Optional() private prefix = 'resident_shared',
    @Inject('CUSTOM_TRANSLATIONS')
    @Optional()
    private customTranslations: { key: string; value: string }[] = []
  ) {}

  handle(params: MissingTranslationHandlerParams): string | undefined {
    const { key, translateService } = params;

    // Check for custom overwrites first
    const overwrite = this.customTranslations?.find(t => t.key === key);
    if (overwrite) {
      // Try to get the overwritten key's translation
      // overwrite.value already contains the full key path (e.g., 'resident_shared.payment_edit.form_iban_l')
      const translation = translateService.instant(overwrite.value, params.interpolateParams);
      if (translation !== overwrite.value) {
        return translation;
      }
    }

    // Try with prefix (skip if key already has prefix or is a customValue placeholder)
    if (!key.includes('customValue') && !key.startsWith(`${this.prefix}.`)) {
      const prefixedKey = `${this.prefix}.${key}`;
      // Use instant to look up the prefixed key
      const translation = translateService.instant(prefixedKey, params.interpolateParams);
      // If translation is different from the key, it was found
      if (translation !== prefixedKey) {
        return translation;
      }
    }

    // Return undefined to show the original key (default behavior)
    return undefined;
  }
}
